// The How it works strip made dynamic (#3194, refined in #3198): the active
// step advances once per breath of its glow (INTERVAL_MS matches the
// mkBreathe keyframes in application.css) and each step carries a
// data-step-state of pending, active or complete that the stylesheet reacts
// to: the active circle fills teal under a breathing halo and a pulse ring,
// completed segments of the connecting line turn teal, and every change
// cross-fades. Hovering a step selects it and holds the loop; leaving
// resumes it. The loop is phase-locked to the breath, so a handover always
// lands at the trough of the glow, and it restarts the server-rendered first
// step's animation at mount so that step is in phase too. The strip's copy
// is server-rendered with the first step already active, so reduced motion
// and no JavaScript both get that static strip, and dispose() puts it back.
// The strip only ever moves forwards on its own clock: a scroll mode that
// let a pinned chapter choose the step (#3201) went with the v10 feedback
// (#3309), which found the steps walking backwards and changing pace.

// One step per breath of the highlight.
export const INTERVAL_MS = 2000;

export type StepState = "pending" | "active" | "complete";

export interface StripHandle {
  readonly active: number;
  setActive(index: number): void;
  pause(): void;
  resume(): void;
  dispose(): void;
}

export function findStrip(root: ParentNode = document): HTMLElement | null {
  return root.querySelector<HTMLElement>("[data-process-strip]");
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Milliseconds until the current breath ends, given when it began.
export function untilNextBreath(
  activatedAt: number,
  now: number,
  interval: number = INTERVAL_MS,
): number {
  const elapsed = Math.max(now - activatedAt, 0);
  return interval - (elapsed % interval);
}

export function mount(strip: HTMLElement, interval: number = INTERVAL_MS): StripHandle {
  const steps = Array.from(strip.querySelectorAll<HTMLElement>("[data-step]"));
  let active = 0;
  let activatedAt = Date.now();
  let paused = false;
  let hovered = false;
  let visible = true;
  let timer = 0;

  const render = () => {
    steps.forEach((step, index) => {
      const state: StepState =
        index < active ? "complete" : index === active ? "active" : "pending";
      step.dataset.stepState = state;
    });
  };

  const setActive = (index: number) => {
    if (steps.length === 0) return;
    active = ((index % steps.length) + steps.length) % steps.length;
    activatedAt = Date.now();
    render();
  };

  const running = () => !paused && !hovered && visible && !document.hidden;

  const stop = () => {
    if (timer === 0) return;
    window.clearTimeout(timer);
    timer = 0;
  };

  // A timeout chain rather than an interval, so resuming after a hold waits
  // for the end of the current breath instead of a full interval.
  const advance = () => {
    timer = 0;
    setActive(active + 1);
    sync();
  };
  const sync = () => {
    if (!running()) {
      stop();
      return;
    }
    if (timer === 0)
      timer = window.setTimeout(advance, untilNextBreath(activatedAt, Date.now(), interval));
  };

  const cleanups: Array<() => void> = [];

  steps.forEach((step, index) => {
    const hold = () => {
      hovered = true;
      if (index !== active) setActive(index);
      sync();
    };
    const release = () => {
      hovered = false;
      sync();
    };
    step.addEventListener("mouseenter", hold);
    step.addEventListener("focusin", hold);
    step.addEventListener("mouseleave", release);
    step.addEventListener("focusout", release);
    cleanups.push(() => {
      step.removeEventListener("mouseenter", hold);
      step.removeEventListener("focusin", hold);
      step.removeEventListener("mouseleave", release);
      step.removeEventListener("focusout", release);
    });
  });

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        sync();
      },
      { threshold: 0 },
    );
    observer.observe(strip);
    cleanups.push(() => observer.disconnect());
  }

  document.addEventListener("visibilitychange", sync);
  cleanups.push(() => document.removeEventListener("visibilitychange", sync));

  // Clear the server-rendered state and flush styles before setting it again,
  // so the first step's breath restarts in phase with the loop.
  for (const step of steps) delete step.dataset.stepState;
  void strip.offsetWidth;
  setActive(0);
  sync();

  return {
    get active() {
      return active;
    },
    setActive,
    pause() {
      paused = true;
      sync();
    },
    resume() {
      paused = false;
      sync();
    },
    // Back to the server-rendered strip: the first step active, the rest unmarked.
    dispose() {
      stop();
      for (const cleanup of cleanups) cleanup();
      steps.forEach((step, index) => {
        if (index === 0) {
          step.dataset.stepState = "active";
        } else {
          delete step.dataset.stepState;
        }
      });
    },
  };
}

// Mounts the strip on the page unless the visitor prefers reduced motion; if
// that preference is switched on later, the strip goes back to static.
export function boot(root: Document = document): StripHandle | null {
  const strip = findStrip(root);
  if (!strip || prefersReducedMotion()) return null;

  const handle = mount(strip);
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  const onChange = (event: MediaQueryListEvent) => {
    if (!event.matches) return;
    handle.dispose();
    query.removeEventListener("change", onChange);
  };
  query.addEventListener("change", onChange);
  return handle;
}
